import { type ReactNode } from "react";
import { Route, Routes } from "react-router-dom";
import { Sidebar, Topbar } from "./Chrome.tsx";
import { AppRoutes } from "./routes.tsx";
import { useColdCore } from "./useColdCore.ts";
import { useWarmCore } from "./useWarmCore.ts";
import { useLiveStreamDriver } from "../state/stream-view.tsx";
import { CommandPalette } from "../chrome/CommandPalette.tsx";
import { DataQualityStrip } from "../chrome/DataQualityStrip.tsx";
import { WakingBanner } from "../chrome/WakingBanner.tsx";
import { UpdateBanner } from "../chrome/UpdateBanner.tsx";
import { ProtectedRoute, useAuth } from "../auth/index.ts";
import { LoginPage } from "../screens/auth/LoginPage.tsx";
import { AccessRequestedScreen } from "../screens/auth/AccessRequestedScreen.tsx";
import { fetchPublicStats } from "../lib/public-stats.ts";
import "./shell.css";

/**
 * The top-level app routing (controlplane-auth task 12.2). It splits the SPA into:
 *
 *  - a PUBLIC `/login` route rendered BARE (no shell chrome, no live stream, no cold-core gating),
 *    so the login page loads with NO session — it is a static asset served by the asset router and
 *    only `/api/*` requires a session (A5.2 / NFR-7 AC1); and
 *  - every OTHER path, wrapped in {@link ProtectedRoute} + the authenticated {@link Shell}. The gate
 *    is the SPA's CLIENT-SIDE reaction to the server's auth contract (the server answers `/api/*`
 *    with 401 JSON, never a redirect; `ProtectedRoute` is what the shell does about an absent
 *    session — it navigates to `/login`). The real boundary stays the BFF.
 *
 * Keeping the shell (its live-stream driver, command palette, chrome) INSIDE the protected branch
 * means an unauthenticated visitor never opens the live socket or renders the operator chrome; they
 * see only the login card. The shell itself still renders unconditionally once authenticated — no
 * data call gates it — so it loads even when `core` is cold.
 */
export function App(): ReactNode {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage loadStats={fetchPublicStats} />} />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <PendingGate>
              <Shell />
            </PendingGate>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

/**
 * The pending-approval route guard (per-team-signin-factors task 14; Requirements 9.2, 9.3).
 *
 * Sits INSIDE {@link ProtectedRoute} (so it only ever sees an authenticated session) and in front of
 * the authenticated {@link Shell}, which means it covers EVERY authenticated route — the `path="*"`
 * branch is the whole app. When the session is pending-only (`auth.pendingApproval`: authenticated
 * but the server resolved no active team, because the user's only non-banned membership(s) are
 * `pending_approval` — see `AuthContext`), it renders the locked {@link AccessRequestedScreen}
 * instead of the shell. So whatever route a pending-only user navigates to, they are kept on the
 * Access Requested screen and never reach a team-scoped view (R9.3). The server independently denies
 * every team-scoped `/api/*` call for such a session (worker task 8); this guard is the SPA's
 * matching client-side behavior, not the security boundary.
 *
 * Once the user is approved (a team membership goes active), the next session read resolves a team,
 * `pendingApproval` flips false, and the shell renders normally with no code change here.
 */
function PendingGate(props: { children: ReactNode }): ReactNode {
  const auth = useAuth();
  if (auth.pendingApproval) return <AccessRequestedScreen />;
  return <>{props.children}</>;
}

/**
 * The authenticated app shell (design 12). A three-row grid: the chrome bar, the persistent
 * data-quality strip, and the scrolling content region with the routed screen. The shell renders
 * unconditionally once a session is confirmed — no data call gates it — so it loads even when `core`
 * is cold or the operator is offline (NFR-7 AC1).
 *
 * Three things sit outside the routed content:
 * - The command palette: a global overlay, invoked with Cmd/Ctrl-K from anywhere.
 * - The data-quality strip: always visible so a view is never read without knowing what is missing.
 * - The "Waking the control plane" banner: shown above the screen whenever a query reports the
 *   cold-core 503 state, with a manual retry (design 12.3).
 *
 * A skip link lets keyboard users jump past the chrome to the content (design 12.4).
 */
function Shell(): ReactNode {
  const coldCore = useColdCore();

  // Warm the per-tenant core container as soon as the shell mounts, so its cold start overlaps the
  // render rather than starting only when the first screen queries (reduces the post-login stall).
  useWarmCore();

  // Run the live stream for the app's lifetime (task 11.3). It is disabled while the core is waking
  // so the client does not race the cold start; once core is up it connects, resumes across drops
  // and keeps the connection indicator and live view fed. Pausing the time-range toggle buffers
  // deltas; the screens read the view, buffered count and gap-disclosed flag from the stream-view
  // store. The driver subscribes to all topics for all services; a screen narrows its own view.
  useLiveStreamDriver({ enabled: !coldCore.waking });

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Sidebar />
      <div className="shell__main">
        <Topbar />
        <DataQualityStrip />
        <main id="main" className="shell__content" tabIndex={-1}>
          <UpdateBanner />
          {coldCore.waking && (
            <WakingBanner onRetry={coldCore.retry} retrying={coldCore.retrying} />
          )}
          <AppRoutes />
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
